import { api } from '@/lib/api/client'
import type { RaspadinhaPublicCampaign } from '@/types/raspadinha-public-campaign'

const SCRATCH_TOKEN_STORAGE_KEY = 'legalprime:scratchToken'

export type ScratchRegisterInput = {
  name: string
  email: string
  phone: string
  document: string
}

export type ScratchRegisterResult = {
  scratch_token?: string
  participant: {
    id: number
    name: string
  }
}

export type ScratchOutcomeResult = {
  outcome: 'contemplated' | 'not_contemplated'
  result_image_url: string
  contemplated_prize: {
    id: string
    title: string
    image_url: string
  } | null
  scratch_id?: number
}

export type ScratchClaimInput = {
  password: string
  postal_code: string
  street: string
  street_number: string
  complement?: string
  neighborhood: string
  city: string
  state: string
}

export type ScratchClaimResult = {
  success: boolean
  receipt: {
    hash: string
    ip_address: string
    signed_at: string
  }
}

export type ScratchReceiptStatus = 'ready' | 'needs_manual_cnpj' | 'retake' | 'duplicate'

export type ScratchReceiptResult = {
  status: ScratchReceiptStatus
  message?: string
}

export type SubmitScratchReceiptInput = {
  scratchToken: string
  image: Blob
  ocrText: string
  manualCnpj?: string
}

export function saveScratchToken(slug: string): void {
  if (typeof window === 'undefined') return
  sessionStorage.setItem(`${SCRATCH_TOKEN_STORAGE_KEY}:${slug}`, '1')
}

export function getScratchToken(slug: string): string | null {
  if (typeof window === 'undefined') return null
  return sessionStorage.getItem(`${SCRATCH_TOKEN_STORAGE_KEY}:${slug}`) === '1' ? 'cookie' : null
}

export function clearScratchToken(slug: string): void {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(`${SCRATCH_TOKEN_STORAGE_KEY}:${slug}`)
}

export async function fetchPublicScratchCampaign(slug: string): Promise<RaspadinhaPublicCampaign> {
  return api.get<RaspadinhaPublicCampaign>(`/api/public/scratch-campaigns/${slug}`)
}

export async function registerScratchParticipant(
  slug: string,
  input: ScratchRegisterInput
): Promise<ScratchRegisterResult> {
  return api.post<ScratchRegisterResult>(`/api/public/scratch-campaigns/${slug}/register`, input)
}

export async function registerScratchParticipantAsDonor(
  slug: string
): Promise<ScratchRegisterResult> {
  return api.post<ScratchRegisterResult>(
    `/api/public/scratch-campaigns/${slug}/register`,
    {}
  )
}

export async function executeScratch(slug: string): Promise<ScratchOutcomeResult> {
  return api.post<ScratchOutcomeResult>(`/api/public/scratch-campaigns/${slug}/scratch`, {})
}

export async function submitScratchReceipt(
  slug: string,
  input: SubmitScratchReceiptInput
): Promise<ScratchReceiptResult> {
  const formData = new FormData()
  formData.append(
    'image',
    input.image,
    input.image instanceof File ? input.image.name : 'cupom.jpg'
  )
  formData.append('ocr_text', input.ocrText)
  if (input.manualCnpj) {
    formData.append('manual_cnpj', input.manualCnpj)
  }

  return api.post<ScratchReceiptResult>(`/api/public/scratch-campaigns/${slug}/receipt`, formData)
}

export async function claimScratchPrize(
  slug: string,
  input: ScratchClaimInput
): Promise<ScratchClaimResult> {
  return api.post<ScratchClaimResult>(`/api/public/scratch-campaigns/${slug}/claim`, {
    ...input,
  })
}
